/* eslint-disable camelcase */
import { useMemo } from "react";
import type { GenreResult, GraphCassetteProps, ResultType } from "./type";
import type { HighlightType } from "../../types/type";
import {
  type SalesAssessmentRankingResults,
  type RankingData,
  type RankTitleName,
  type DistributionItem,
} from "./mocks";

// ============================================================================
// 型定義
// ============================================================================

type ResultRank = "gold" | "silver" | "bronze" | "normal" | "potential";

type ResultTypeConfig = {
  resultType: ResultType;
  genre: string;
  graphTitle: (jobTypeName: string) => string;
  formatAverage: (value: number) => string;
  formatLabel: (item: DistributionItem) => string;
};

export type ConvertedResults = {
  genreResults: GenreResult[];
  graphCassetteProps: GraphCassetteProps[];
};

// ============================================================================
// 変換ユーティリティ
// ============================================================================

/**
 * サーバーからの RankTitleName を フロントエンドの HighlightType に変換する
 * - gold/silver/bronze はそのまま
 * - black は normal（平均的）
 * - orange は potential（のびしろ）
 */
const convertRankTitleToHighlightType = (rankTitle: RankTitleName): HighlightType => {
  switch (rankTitle) {
    case "gold":
      return "gold";
    case "silver":
      return "silver";
    case "bronze":
      return "bronze";
    case "black":
      return "normal";
    case "orange":
      return "potential";
    default:
      return rankTitle satisfies never;
  }
};

/**
 * ResultRank に対応するラベルテキストを取得する
 */
const getLabelText = (rank: ResultRank): string | undefined => {
  switch (rank) {
    case "gold":
      return "ゴールドランク";
    case "silver":
      return "シルバーランク";
    case "bronze":
      return "ブロンズランク";
    case "potential":
      return "のびしろ";
    case "normal":
      return undefined;
    default:
      return rank satisfies never;
  }
};

/**
 * 各指標の設定
 */
const RESULT_TYPE_CONFIGS: Record<keyof SalesAssessmentRankingResults, ResultTypeConfig> = {
  average_number_of_customers: {
    resultType: "numberOfCustomers",
    genre: "対応客数",
    graphTitle: (jobTypeName) => `対応客数 ${jobTypeName}`,
    formatAverage: (value) => `${value.toLocaleString()}人/月`,
    formatLabel: (item) => (item.max === null ? `${item.min}人〜` : `${item.min}〜${item.max}人`),
  },
  average_nomination_ratio: {
    resultType: "averageNominationRatio",
    genre: "指名割合",
    graphTitle: (jobTypeName) => `指名割合 ${jobTypeName}`,
    formatAverage: (value) => `${value}%`,
    formatLabel: (item) => (item.max === null ? `${item.min}%〜` : `${item.min}〜${item.max}%`),
  },
  average_customer_spending: {
    resultType: "averageCustomerSpending",
    genre: "顧客単価",
    graphTitle: (jobTypeName) => `顧客単価 ${jobTypeName}`,
    formatAverage: (value) => `${value.toLocaleString()}円`,
    formatLabel: (item) =>
      item.max === null
        ? `${item.min.toLocaleString()}円〜`
        : `${item.min.toLocaleString()}〜${item.max.toLocaleString()}円`,
  },
  average_personal_sales: {
    resultType: "averagePersonalSales",
    genre: "個人売上",
    graphTitle: (jobTypeName) => `個人売上 ${jobTypeName}`,
    formatAverage: (value) => {
      if (value >= 10000) {
        return `${(value / 10000).toFixed(1)}万円/月`;
      }
      return `${value.toLocaleString()}円/月`;
    },
    formatLabel: (item) => {
      const formatValue = (v: number) =>
        v >= 10000 ? `${(v / 10000).toFixed(0)}万円` : `${v.toLocaleString()}円`;
      return item.max === null
        ? `${formatValue(item.min)}〜`
        : `${formatValue(item.min)}〜${formatValue(item.max)}`;
    },
  },
};

const RANKING_KEYS: (keyof SalesAssessmentRankingResults)[] = [
  "average_number_of_customers",
  "average_nomination_ratio",
  "average_customer_spending",
  "average_personal_sales",
];

// ============================================================================
// Hook
// ============================================================================

/**
 * SalesAssessmentRankingResults を Presentation 用のデータに変換する
 */
export const useConvertedResults = (
  salesAssessmentRankingResults: SalesAssessmentRankingResults,
  jobTypeName: string,
): ConvertedResults =>
  useMemo(() => {
    return RANKING_KEYS.reduce<ConvertedResults>(
      (acc, key) => {
        const data: RankingData = salesAssessmentRankingResults[key];
        const config = RESULT_TYPE_CONFIGS[key];
        const rankTitle = data.rank_title.title;
        const rank = convertRankTitleToHighlightType(rankTitle);
        const percentileMin = data.rank_title.percentile_min;
        const percentileMax = data.rank_title.percentile_max;
        const userRangeIndex = data.distribution.findIndex((item) => item.is_user_range);

        // GenreResult
        acc.genreResults.push({
          genre: config.genre,
          tier: rank,
          position: data.user_rank,
        });

        // GraphCassetteProps
        acc.graphCassetteProps.push({
          jobTypeName,
          average: config.formatAverage(data.industry_average),
          resultType: config.resultType,
          result: {
            title: config.genre,
            rankNum: data.user_rank,
            total: data.total_count,
            labelText: getLabelText(rank),
            rank: rank === "normal" ? undefined : rank,
            percentileMin,
            percentileMax,
          },
          reportGraph: {
            title: config.graphTitle(jobTypeName),
            labels: data.distribution.map((item) => config.formatLabel(item)),
            values: data.distribution.map((item) => item.count),
            highlights: userRangeIndex >= 0 ? [{ type: rank, index: userRangeIndex }] : [],
          },
        });

        return acc;
      },
      { genreResults: [], graphCassetteProps: [] },
    );
  }, [salesAssessmentRankingResults, jobTypeName]);
